import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Truck, MapPin, UploadSimple, Storefront, MagnifyingGlass } from '@phosphor-icons/react';
import { useBranding } from '../lib/branding';
import { useAuth } from '../lib/auth';
import API from '../lib/api';
import { optimizeImg } from '../lib/image';
import { catalogCache } from '../lib/catalogCache';

const Logo = () => (
  <img src="/icons/icon-192.png" alt="PrintingKit" width="36" height="36" className="w-9 h-9 object-contain" />
);

const STEPS = [
  { icon: Storefront, title: 'Pick product', desc: 'Material, size, quantity.' },
  { icon: UploadSimple, title: 'Upload file', desc: 'PDF, JPG, PNG — or paste link.' },
  { icon: MapPin, title: 'Nearest partner', desc: 'Auto-matched by pincode.' },
  { icon: Truck, title: 'Delivered', desc: 'Tracked to your doorstep.' },
];

const STATS = [
  { value: 100, suffix: '+', label: 'Partners' },
  { value: 33, suffix: 'K+', label: 'Orders' },
  { value: 50, suffix: '+', label: 'Pincodes' },
];

const useCountUp = (target, active, duration = 1500) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf; const start = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return n;
};

const StatsBox = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) setVisible(true); });
    }, { threshold: 0.35 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  const n0 = useCountUp(STATS[0].value, visible);
  const n1 = useCountUp(STATS[1].value, visible);
  const n2 = useCountUp(STATS[2].value, visible);
  const vals = [n0, n1, n2];
  return (
    <div ref={ref} className="pk-card p-5 grid grid-cols-3 gap-4 text-center items-end">
      {STATS.map((s, i) => (
        <div key={s.label} className="flex flex-col items-center justify-end">
          <div className="font-stat text-[26px] leading-none whitespace-nowrap">
            {vals[i]}<span style={{ color: 'var(--pk-primary)' }}>{s.suffix}</span>
          </div>
          <div className="text-[9px] tracking-[0.24em] uppercase text-neutral-500 mt-3">{s.label}</div>
        </div>
      ))}
    </div>
  );
};

// Footer shown only when scroll reaches end
const EndFooter = ({ footerNote, poweredBy }) => {
  // footer_note first line = brand line (e.g. "PRINTINGKIT © 2026 · All rights reserved")
  // We render a 3-line structured footer: brand → tagline → powered-by
  const raw = (footerNote || 'PRINTINGKIT © 2026 · All rights reserved').trim();
  const parts = raw.split('·').map(s => s.trim()).filter(Boolean);
  const brand = parts[0] || 'PRINTINGKIT © 2026';
  const rights = parts.slice(1).join(' · ') || 'All rights reserved';
  const company = (poweredBy || 'MACFINTI SOLUTIONS PVT LTD').trim();
  return (
    <div className="mt-10 pt-6 pb-8 text-center border-t-2 border-black">
      <div className="font-display text-[18px] tracking-[0.22em] uppercase leading-none text-[color:var(--pk-black)]" data-testid="footer-brand">
        {brand}
      </div>
      <div className="mt-3 text-[10px] tracking-[0.12em] text-neutral-500">
        {rights} <span className="mx-1">·</span> Powered by
      </div>
      <div className="mt-2 font-display text-[15px] tracking-[0.14em] uppercase leading-none" data-testid="footer-company">
        {company}
      </div>
      <Link to="/partner-signup" className="mt-6 inline-block text-[10px] text-neutral-400 hover:text-neutral-600 underline underline-offset-4" data-testid="partner-join-link">
        Are you a printer? Join our network →
      </Link>
      <div className="mt-3 flex items-center justify-center gap-3">
        <Link to="/privacy" className="inline-block text-[10px] text-neutral-400 hover:text-neutral-600 underline underline-offset-4" data-testid="footer-privacy-link">
          Privacy Policy
        </Link>
        <span className="text-[10px] text-neutral-300">·</span>
        <Link to="/terms" className="inline-block text-[10px] text-neutral-400 hover:text-neutral-600 underline underline-offset-4" data-testid="footer-terms-link">
          Terms of Service
        </Link>
      </div>
    </div>
  );
};

const Landing = () => {
  const { branding } = useBranding();
  const { user } = useAuth();
  const nav = useNavigate();
  const [products, setProducts] = useState(() => catalogCache.get()); // paint cached instantly, null = empty cache → spinner

  useEffect(() => {
    // Lightweight (summary=1) catalog — strips variant_groups/description for ~90% smaller payload.
    API.get('/catalog', { params: { summary: 1 } })
      .then(r => { setProducts(r.data); catalogCache.set(r.data); })
      .catch(() => { if (!catalogCache.get()) setProducts([]); });
  }, []);

  const height = branding.banners_height === 'small' ? 90 : branding.banners_height === 'large' ? 170 : 130;
  const cols = branding.banners_columns || 2;

  return (
    <div>
      {/* STICKY HEADER — glass finish, floats with rounded corners (matches Contact FAB) */}
      <div
        className="sticky top-2 z-20 mx-2 rounded-2xl"
        style={{
          background: 'color-mix(in srgb, #ffffff 70%, transparent)',
          backdropFilter: 'blur(14px) saturate(180%)',
          WebkitBackdropFilter: 'blur(14px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          boxShadow: '0 8px 24px -8px rgba(0, 0, 0, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
        }}
      >
        <div className="px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {branding.logo_data_url
              ? <img src={branding.logo_data_url} alt="logo" className="w-9 h-9 object-contain" />
              : <Logo />}
            <div>
              <div className="font-display text-[16px] leading-none">{branding.logo_text}</div>
              <div className="text-[8px] tracking-[0.22em] uppercase text-neutral-500 mt-1">{branding.logo_tagline}</div>
            </div>
          </div>
          {user
            ? (
              <Link to="/categories" className="p-2 -mr-1 ml-auto rounded-full transition-transform active:scale-90" aria-label="Search products" data-testid="header-search-link">
                <MagnifyingGlass size={20} weight="bold" />
              </Link>
            )
            : (
              <div className="flex items-center gap-2 ml-auto">
                <Link to="/categories" className="p-2 rounded-full transition-transform active:scale-90" aria-label="Search products" data-testid="header-search-link">
                  <MagnifyingGlass size={20} weight="bold" />
                </Link>
                <Link to="/login" className="pk-chip" data-testid="header-login-link">Login</Link>
              </div>
            )
          }
        </div>
      </div>

      {branding.hero_banner_data_url && (() => {
        const t = branding.hero_banner_link_type;
        const v = (branding.hero_banner_link_id || '').trim();
        let to = null;
        if (v) {
          if (t === 'product') to = `/shop/${v}`;
          else if (t === 'category') to = `/shop?category=${v}`;
          else if (t === 'url') to = v;
        }
        const banner = <img src={branding.hero_banner_data_url} alt="banner" className="w-full h-full object-cover" />;
        const wrapClass = `w-full aspect-square overflow-hidden ${to ? 'block cursor-pointer' : ''}`;
        if (!to) return <div className={wrapClass}>{banner}</div>;
        // External URL vs internal route
        if (/^https?:\/\//i.test(to)) {
          return <a href={to} target="_blank" rel="noopener noreferrer" className={wrapClass} data-testid="hero-banner-link">{banner}</a>;
        }
        return <Link to={to} className={wrapClass} data-testid="hero-banner-link">{banner}</Link>;
      })()}

      <div className="px-5 pt-6 reg-mark">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— {branding.hero_overline}</div>
        <h1 className="mt-3 font-display text-[44px] leading-[1.25]">
          <span className="inline-block leading-none">{branding.hero_line_1}</span><br/>
          <span className="pk-highlight inline-block leading-none">{branding.hero_line_2}</span><br/>
          <span className="inline-block leading-none" style={{ color: 'var(--pk-primary)' }}>{branding.hero_line_3}</span>
        </h1>
        <p className="mt-4 text-[13px] leading-relaxed text-neutral-700">{branding.hero_subtext}</p>
        <div className="mt-5 space-y-2">
          <button className="pk-btn w-full" onClick={() => nav('/categories')} data-testid="cta-browse">
            Browse catalog <ArrowRight size={16} weight="bold" />
          </button>
          <button className="pk-btn pk-btn-outline w-full" onClick={() => nav('/enterprise')} data-testid="cta-enterprise">
            Enterprise / Bulk
          </button>
        </div>
      </div>

      {branding.banners && branding.banners.length > 0 && (
        <div className="px-5 mt-6">
          <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
            {branding.banners.map((b, i) => (
              <Link
                key={i}
                to={b.link_type === 'category' ? `/shop?category=${b.link_id}` : b.link_type === 'product' ? `/shop/${b.link_id}` : '/categories'}
                className="pk-border overflow-hidden brutal-shadow-sm block"
                style={{ height }}
              >
                <img src={b.image_data_url} alt={b.alt || 'offer'} className="w-full h-full object-cover" />
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="px-5 mt-7">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— Featured</div>
            <div className="font-display text-[24px] mt-1">Top Products</div>
          </div>
          <Link to="/categories" className="text-[11px] tracking-[0.22em] font-bold uppercase" data-testid="see-all-link">All →</Link>
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3">
          {products === null && Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="pk-border brutal-shadow-sm bg-white animate-pulse" data-testid={`landing-product-skeleton-${i}`}>
              <div className="aspect-square bg-neutral-200"></div>
              <div className="p-3 border-t-2 border-black space-y-2">
                <div className="h-2 bg-neutral-200 w-16"></div>
                <div className="h-3 bg-neutral-200 w-24"></div>
                <div className="h-3 bg-neutral-200 w-20"></div>
              </div>
            </div>
          ))}
          {(products || []).slice(0, 4).map((p, i) => (
            <Link key={p.product_id} to={`/shop/${p.product_id}`} className="pk-border brutal-shadow-sm bg-white">
              <div className="aspect-square overflow-hidden bg-neutral-100">
                {p.image && (
                  <img
                    src={optimizeImg(p.image, 400)}
                    alt={p.name}
                    width="400"
                    height="400"
                    loading={i < 2 ? 'eager' : 'lazy'}
                    fetchpriority={i < 2 ? 'high' : 'auto'}
                    decoding="async"
                    className="w-full h-full object-cover"
                    onLoad={(e) => { e.currentTarget.style.opacity = '1'; }}
                    style={{ opacity: 0, transition: 'opacity 200ms ease-out' }}
                  />
                )}
              </div>
              <div className="p-3 border-t-2 border-black">
                <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-500">{p.pricing_mode === 'per_sqft' ? 'per sqft' : 'per unit'}</div>
                <div className="font-bold text-[13px] mt-1 leading-tight">{p.name}</div>
                <div className="font-mono text-[12px] mt-1">From <span className="font-bold">₹{p.base_price}</span></div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="px-5 mt-8">
        <StatsBox />
      </div>

      <div className="px-5 mt-8">
        <div className="text-[10px] tracking-[0.24em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— How it works</div>
        <div className="font-display text-[24px] mt-1">Four steps to your door.</div>
        <div className="mt-4 space-y-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Link
                key={i}
                to="/categories"
                className="pk-card p-3 flex items-center gap-3 hover:brutal-shadow-magenta transition-shadow group active:brutal-shadow-yellow"
                data-testid={`how-step-${i + 1}`}
              >
                <div className="font-display text-[26px] w-8 text-neutral-300 transition-colors group-hover:text-[color:var(--pk-yellow)] group-active:text-[color:var(--pk-yellow)] group-focus:text-[color:var(--pk-yellow)]">{String(i+1).padStart(2, '0')}</div>
                <Icon size={22} weight="duotone" color="var(--pk-primary)" />
                <div className="flex-1">
                  <div className="font-bold text-[14px]">{s.title}</div>
                  <div className="text-[12px] text-neutral-600">{s.desc}</div>
                </div>
                <ArrowRight size={14} weight="bold" className="text-neutral-400" />
              </Link>
            );
          })}
        </div>
      </div>

      <EndFooter footerNote={branding.footer_note} poweredBy={branding.powered_by} />
    </div>
  );
};

export default Landing;
